import { useEffect, useState } from 'react';
import './player-view.styles.scss';
import { Link } from 'react-router-dom';
import { listenToDoc } from '@/utils/getDataFromDb';
import { positions } from '@/utils/positions';
import { levels } from '@/utils/levels';
import { formatMoney } from '@shared/contracts.js';
import SortTh from '@/components/SHARED/sort-th/sort-th.component';
import { useSortable } from '@/utils/useSortable';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import TeamName from '@/components/SHARED/team-name/team-name.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

const OFFENSE = [
    ['off_3pt', '3-point shooting'],
    ['off_shooting', 'Mid-range shooting'],
    ['off_drive', 'Driving'],
    ['off_passing', 'Passing'],
    ['off_rebounds', 'Offensive rebounding'],
    ['off_smarts', 'Offensive IQ'],
];
const DEFENSE = [
    ['def_speed', 'Perimeter defense'],
    ['def_steals', 'Steals'],
    ['def_smarts', 'Defensive IQ'],
    ['def_paint', 'Interior defense'],
    ['def_blocks', 'Shot blocking'],
    ['def_rebounds', 'Defensive rebounding'],
];

// One player: ratings, season averages and game log.
// teams: summary/teams map (opponent names). teamLink / gameLink -> paths.
// hidden: admin tools only, this player's hidden traits {chemistry,
// potential, leadership}; managers never receive them.
const PlayerView = ({ leagueId, teamId, playerId, teams, teamLink, gameLink, hidden }) => {

    const [ team, setTeam ] = useState(undefined);
    const [ log, setLog ] = useState(null);
    const logSorter = useSortable();
    const careerSorter = useSortable();

    useEffect(() => {
        const listeners = [];
        listenToDoc({
            'path': `leagues/${leagueId}/teams/${teamId}`,
            'callback': setTeam,
            'handleListener': (u) => listeners.push(u),
        });
        listenToDoc({
            'path': `leagues/${leagueId}/logs/${teamId}`,
            'callback': (d) => setLog(d?.players?.[playerId] ?? []),
            'handleListener': (u) => listeners.push(u),
        });
        return () => listeners.forEach(u => u());
    }, [leagueId, teamId, playerId])

    if (team === undefined) return <div className='center-col'><ThreeDotsLoader /></div>;
    const p = team?.players?.[playerId];
    if (!p) return <p className='meta'>This player isn&apos;t on the team anymore.</p>;

    const s = p.season ?? {};
    const per = (key) => (s.gp ? (s[key] / s.gp).toFixed(1) : '-');
    const pct = (made, att) => (att ? `${(made / att * 100).toFixed(1)}%` : '-');
    const pos = positions[p.pos1 - 1].short + (p.pos2 ? `/${positions[p.pos2 - 1].short}` : '');
    const klass = p.year ? `${levels[p.year - 1].long}${p.redshirt ? ' (redshirt)' : p.fifthYear ? ' (5th year)' : ''}` : `Age ${p.age}`;
    const height = `${Math.floor(p.height / 12)}'${p.height % 12}"`;
    const games = logSorter.apply([...(log ?? [])].reverse(), {
        day: g => g.d, opp: g => teams?.[g.o]?.abbr ?? g.o, result: g => g.ts - g.os, min: g => g.min,
        pts: g => g.pts, reb: g => g.oreb + g.dreb, ast: g => g.ast, stl: g => g.stl, blk: g => g.blk,
        tov: g => g.tov, fg: g => g.fgm, tp: g => g.tpm, ft: g => g.ftm, pf: g => g.pf,
    });
    // Finished seasons, newest first.
    const perSeason = (c, x) => (c.season.gp ? x / c.season.gp : null);
    const career = careerSorter.apply([...(p.career ?? [])].sort((a, b) => b.year - a.year), {
        year: c => c.year, team: c => c.abbr, klass: c => c.class ?? c.age, ovr: c => c.overall,
        gp: c => c.season.gp, min: c => perSeason(c, c.season.min), pts: c => perSeason(c, c.season.pts),
        reb: c => perSeason(c, c.season.oreb + c.season.dreb), ast: c => perSeason(c, c.season.ast),
        stl: c => perSeason(c, c.season.stl), blk: c => perSeason(c, c.season.blk),
        fg: c => (c.season.fga ? c.season.fgm / c.season.fga : null),
        tp: c => (c.season.tpa ? c.season.tpm / c.season.tpa : null),
        ft: c => (c.season.fta ? c.season.ftm / c.season.fta : null),
    });
    const logTh = (k, label, first, className = 'num') => <SortTh k={k} label={label} sorter={logSorter} first={first} className={className} />;
    const careerTh = (k, label, first, className = 'num') => <SortTh k={k} label={label} sorter={careerSorter} first={first} className={className} />;
    const honors = career.flatMap(c => (c.awards ?? []).map(award => ({ year: c.year, award })));

    return (
        <div className='player-view'>
            <div className='g-card player-head'>
                <div className='player-head-text'>
                    <h2>{p.fName} {p.lName}</h2>
                    <div className='meta'>{pos} · {klass} · {height}</div>
                    {p.contract &&
                        <div className='meta'>
                            Contract: {formatMoney(p.contract.salary)}{p.contract.years ? `, ${p.contract.years} year${p.contract.years === 1 ? '' : 's'} left` : ', expiring'}
                        </div>
                    }
                    {p.drafted &&
                        <div className='meta'>Drafted: Season {p.drafted.year}, round {p.drafted.round}, pick #{p.drafted.pick} ({p.drafted.abbr})</div>
                    }
                    <Link to={teamLink(teamId)} className='player-team'>
                        <TeamBadge team={team} size='sm' /> <span><TeamName team={team} /></span>
                    </Link>
                    {p.injury?.gamesOut > 0 &&
                        <div className='player-injury'>Injured: out {p.injury.gamesOut} more game{p.injury.gamesOut === 1 ? '' : 's'}</div>
                    }
                </div>
                <div className='player-big-ratings'>
                    <Big label='Overall' value={p.overall} />
                    <Big label='Offense' value={p.off_quality} />
                    <Big label='Defense' value={p.def_quality} />
                    <Big label='Stamina' value={p.stamina} />
                </div>
                {hidden &&
                    <div className='player-hidden'>
                        Hidden from managers: chemistry {hidden.chemistry} · potential {hidden.potential} · leadership {hidden.leadership}
                    </div>
                }
            </div>

            <div className='player-ratings'>
                <RatingList title='Offense' items={OFFENSE} player={p} />
                <RatingList title='Defense' items={DEFENSE} player={p} />
            </div>

            <div className='g-card'>
                <h3>Season</h3>
                <div className='season-line'>
                    <Stat label='GP' value={s.gp ?? 0} />
                    <Stat label='GS' value={s.gs ?? 0} />
                    <Stat label='MIN' value={per('min')} />
                    <Stat label='PTS' value={per('pts')} />
                    <Stat label='REB' value={s.gp ? ((s.oreb + s.dreb) / s.gp).toFixed(1) : '-'} />
                    <Stat label='AST' value={per('ast')} />
                    <Stat label='STL' value={per('stl')} />
                    <Stat label='BLK' value={per('blk')} />
                    <Stat label='TO' value={per('tov')} />
                    <Stat label='FG%' value={pct(s.fgm, s.fga)} />
                    <Stat label='3P%' value={pct(s.tpm, s.tpa)} />
                    <Stat label='FT%' value={pct(s.ftm, s.fta)} />
                </div>
            </div>

            {p.playoffs?.gp > 0 &&
                <div className='g-card'>
                    <h3>Playoffs</h3>
                    <div className='season-line'>
                        <Stat label='GP' value={p.playoffs.gp} />
                        <Stat label='PTS' value={(p.playoffs.pts / p.playoffs.gp).toFixed(1)} />
                        <Stat label='REB' value={((p.playoffs.oreb + p.playoffs.dreb) / p.playoffs.gp).toFixed(1)} />
                        <Stat label='AST' value={(p.playoffs.ast / p.playoffs.gp).toFixed(1)} />
                        <Stat label='FG%' value={pct(p.playoffs.fgm, p.playoffs.fga)} />
                    </div>
                </div>
            }

            {honors.length > 0 &&
                <div className='g-card player-honors'>
                    <h3>Awards</h3>
                    <ul>
                        {honors.map(h => <li key={`${h.year}-${h.award}`}><span className='honor-year'>Season {h.year}</span><span>{h.award}</span></li>)}
                    </ul>
                </div>
            }

            {career.length > 0 &&
                <div className='g-card'>
                    <h3>Career</h3>
                    <div className='table-scroll'>
                        <table className='g-table game-log'>
                            <thead>
                                <tr>
                                    {careerTh('year', 'Season', 'desc', '')}
                                    {careerTh('team', 'Team', 'asc', '')}
                                    {careerTh('klass', p.year ? 'Yr' : 'Age')}
                                    {careerTh('ovr', 'Ovr')}
                                    {careerTh('gp', 'GP')}
                                    {careerTh('min', 'Min')}
                                    {careerTh('pts', 'Pts')}
                                    {careerTh('reb', 'Reb')}
                                    {careerTh('ast', 'Ast')}
                                    {careerTh('stl', 'Stl')}
                                    {careerTh('blk', 'Blk')}
                                    {careerTh('fg', 'FG%')}
                                    {careerTh('tp', '3P%')}
                                    {careerTh('ft', 'FT%')}
                                </tr>
                            </thead>
                            <tbody>
                                {career.map(c => {
                                    const cs = c.season;
                                    const avg = (x) => (cs.gp ? (x / cs.gp).toFixed(1) : '-');
                                    return (
                                        <tr key={c.year}>
                                            <td>{c.year}</td>
                                            <td>{c.abbr}</td>
                                            <td className='num'>{c.class ? levels[c.class - 1]?.short : c.age}</td>
                                            <td className='num'>{c.overall}</td>
                                            <td className='num'>{cs.gp}</td>
                                            <td className='num'>{avg(cs.min)}</td>
                                            <td className='num strong'>{avg(cs.pts)}</td>
                                            <td className='num'>{avg(cs.oreb + cs.dreb)}</td>
                                            <td className='num'>{avg(cs.ast)}</td>
                                            <td className='num'>{avg(cs.stl)}</td>
                                            <td className='num'>{avg(cs.blk)}</td>
                                            <td className='num'>{pct(cs.fgm, cs.fga)}</td>
                                            <td className='num'>{pct(cs.tpm, cs.tpa)}</td>
                                            <td className='num'>{pct(cs.ftm, cs.fta)}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                    <p className='meta log-note'>Per-game averages. Ovr is the rating at the end of that season.</p>
                </div>
            }

            <div className='g-card'>
                <h3>Game log</h3>
                {log === null ? <ThreeDotsLoader /> :
                    games.length === 0 ? <p className='meta'>No games played yet.</p> :
                    <div className='table-scroll'>
                        <table className='g-table game-log'>
                            <thead>
                                <tr>
                                    {logTh('day', 'Day', 'desc', '')}
                                    {logTh('opp', 'Opp', 'asc', '')}
                                    {logTh('result', 'Result', 'desc', '')}
                                    {logTh('min', 'Min')}
                                    {logTh('pts', 'Pts')}
                                    {logTh('reb', 'Reb')}
                                    {logTh('ast', 'Ast')}
                                    {logTh('stl', 'Stl')}
                                    {logTh('blk', 'Blk')}
                                    {logTh('tov', 'TO', 'asc')}
                                    {logTh('fg', 'FG')}
                                    {logTh('tp', '3PT')}
                                    {logTh('ft', 'FT')}
                                    {logTh('pf', 'PF', 'asc')}
                                </tr>
                            </thead>
                            <tbody>
                                {games.map(g => (
                                    <tr key={g.g}>
                                        <td>{g.d}{g.p ? <span className='post-tag' title='Postseason'> P</span> : null}</td>
                                        <td>
                                            <Link to={teamLink(g.o)} className='log-opp'>
                                                {g.h || g.n ? 'vs' : '@'} {teams?.[g.o]?.abbr ?? g.o}
                                            </Link>
                                        </td>
                                        <td>
                                            <Link to={gameLink(g.g)} className={`log-result ${g.w ? 'win' : 'loss'}`}>
                                                {g.w ? 'W' : 'L'} {g.ts}-{g.os}
                                            </Link>
                                        </td>
                                        <td className='num'>{Math.round(g.min)}{g.gs ? '*' : ''}</td>
                                        <td className='num strong'>{g.pts}</td>
                                        <td className='num'>{g.oreb + g.dreb}</td>
                                        <td className='num'>{g.ast}</td>
                                        <td className='num'>{g.stl}</td>
                                        <td className='num'>{g.blk}</td>
                                        <td className='num'>{g.tov}</td>
                                        <td className='num'>{g.fgm}-{g.fga}</td>
                                        <td className='num'>{g.tpm}-{g.tpa}</td>
                                        <td className='num'>{g.ftm}-{g.fta}</td>
                                        <td className='num'>{g.pf}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                }
                {games.length > 0 && <p className='meta log-note'>* Started{games.some(g => g.p) ? ' · P Postseason' : ''}</p>}
            </div>
        </div>
    )
}

const Big = ({ label, value }) => (
    <div className='big-rating'>
        <span className='big-value'>{value}</span>
        <span className='meta'>{label}</span>
    </div>
);

const Stat = ({ label, value }) => (
    <div className='stat'>
        <span className='stat-value'>{value}</span>
        <span className='meta'>{label}</span>
    </div>
);

const RatingList = ({ title, items, player }) => (
    <div className='g-card rating-list'>
        <h3>{title}</h3>
        {items.map(([key, label]) => (
            <div key={key} className='rating-row'>
                <span className='rating-label'>{label}</span>
                <span className='rating-bar' aria-hidden='true'>
                    <span style={{ width: `${player[key]}%` }}></span>
                </span>
                <span className='rating-value'>{player[key]}</span>
            </div>
        ))}
    </div>
);

export default PlayerView
